import React, { useState } from "react";
import { Link } from "react-router-dom";
import OTPInput from "react-otp-input";
import minilogo from "assets/images/logo.png";
import navigationAction from "redux/navigation/actions";
import Loader from "components/Loader";
import {
  requestDeleteAccountOtp,
  confirmDeleteAccount
} from "services/deleteAccountServices";

const { success, error } = navigationAction;

const STEP_EMAIL = "email";
const STEP_OTP = "otp";
const STEP_DONE = "done";
const OTP_LENGTH = 6;
const GENERIC_ERROR = "Something went wrong. Please try again.";

function DeleteAccount() {
  const [step, setStep] = useState(STEP_EMAIL);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);

  const callApi = async (request, onSuccess, onError) => {
    setLoading(true);
    try {
      const response = await request();
      if (response?.success) {
        onSuccess(response);
      } else {
        error(response?.message || GENERIC_ERROR);
        onError && onError();
      }
    } catch (e) {
      error(GENERIC_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const sendCode = () =>
    callApi(
      () => requestDeleteAccountOtp({ email: email.trim() }),
      response => {
        success(response.message);
        setOtp("");
        setStep(STEP_OTP);
      }
    );

  const handleSendCode = e => {
    e.preventDefault();
    sendCode();
  };

  const handleDelete = e => {
    e.preventDefault();
    callApi(
      () => confirmDeleteAccount({ email: email.trim(), otp }),
      () => setStep(STEP_DONE),
      () => setOtp("")
    );
  };

  const changeEmail = () => {
    setOtp("");
    setStep(STEP_EMAIL);
  };

  return (
    <div className="tnc-header">
      <div className="tnc-heading w-100">
        <div className="logo-text simple-text fs-20 bold-text">
          <img src={minilogo} alt="Minute Log" />
        </div>
      </div>
      <div className="container tnc-container">
        <div className="row justify-content-center">
          <div className="col-md-10 col-lg-7">
            <h4 className="mb-3 mt-2">Delete your Minute Log account</h4>

            {step !== STEP_DONE && (
              <>
                <p>
                  You can delete your Minute Log account here, even if you no
                  longer have the app installed. Enter the email address you
                  sign in with and we'll send you a verification code.
                </p>
                <h6 className="mt-4">
                  What happens when you delete your account
                </h6>
                <ul>
                  <li>
                    Your account is closed straight away. You're signed out on
                    every device and can't sign in to it again.
                  </li>
                  <li>
                    If you're an account owner with a subscription, it's
                    cancelled straight away. The rest of the current billing
                    period isn't refunded.
                  </li>
                  <li>
                    If you're an account owner with users, you need to delete
                    those users before you can delete your account.
                  </li>
                  <li>
                    Records linked to your account are kept as described in
                    our <Link to="/privacy-policy">Privacy Policy</Link>.
                  </li>
                </ul>
              </>
            )}

            {step === STEP_EMAIL && (
              <form onSubmit={handleSendCode} className="mt-4">
                <div className="form-group">
                  <label htmlFor="email">Email address</label>
                  <input
                    type="email"
                    id="email"
                    required
                    maxLength={60}
                    className="form-control react-form-input"
                    placeholder="Enter email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                  />
                </div>
                <button
                  type="submit"
                  className="btn form-button siteenterbutton mt-2"
                  disabled={loading}
                >
                  Send verification code
                </button>
              </form>
            )}

            {step === STEP_OTP && (
              <form onSubmit={handleDelete} className="mt-4">
                <p>
                  If <strong>{email.trim()}</strong> belongs to a Minute Log
                  account, we've sent a {OTP_LENGTH}-digit code to it. The code
                  expires in 15 minutes. Enter it below to delete your account.
                  This can't be undone.
                </p>
                <OTPInput
                  value={otp}
                  onChange={setOtp}
                  numInputs={OTP_LENGTH}
                  inputType="tel"
                  shouldAutoFocus
                  inputStyle={{
                    height: "50px",
                    width: "45px",
                    margin: "0 4px",
                    border: "1px solid #ccc",
                    borderRadius: "5px"
                  }}
                  containerStyle={{ marginBottom: "24px", marginTop: "16px" }}
                  renderInput={props => <input {...props} />}
                />
                <button
                  type="submit"
                  className="btn btn-danger"
                  disabled={loading || otp.length !== OTP_LENGTH}
                >
                  Delete my account
                </button>
                <div className="mt-3">
                  <button
                    type="button"
                    className="btn btn-link pl-0"
                    onClick={sendCode}
                    disabled={loading}
                  >
                    Resend code
                  </button>
                  <button
                    type="button"
                    className="btn btn-link"
                    onClick={changeEmail}
                    disabled={loading}
                  >
                    Use a different email
                  </button>
                </div>
              </form>
            )}

            {step === STEP_DONE && (
              <div className="mt-2">
                <h5>Your account has been deleted</h5>
                <p>
                  You've been signed out on all devices. You can now uninstall
                  the Minute Log app.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      {loading && <Loader />}
    </div>
  );
}

export default DeleteAccount;
